import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { useFontes } from '../../hooks/useFontes';
import { Fonte } from '../../types/fonte';
import { HeaderList } from '../../components/HeaderList';
import { TableList } from '../../components/TableList';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { Pagination } from '../../components/Pagination';
import { useFontCache } from '../../hooks/useFontCache';

const FontesLista: React.FC = () => {
  const [busca, setBusca] = useState('');
  const [fonteParaExcluir, setFonteParaExcluir] = useState<Fonte | null>(null);
  const { fontes, listLoading, excluirFonte } = useFontes();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { loadFontFromCache } = useFontCache();
  const [fontesCarregadas, setFontesCarregadas] = useState<Set<string>>(new Set());

  const handleExcluir = async (fonte: Fonte) => {
    setFonteParaExcluir(fonte);
  };

  const confirmarExclusao = async () => {
    if (fonteParaExcluir) {
      await excluirFonte(fonteParaExcluir.id);
      setFonteParaExcluir(null);
    }
  };

  const fontesFiltradas = (fontes || []).filter(fonte =>
    busca === '' || fonte.nome.toLowerCase().includes(busca.toLowerCase())
  );

  // Cálculo da paginação
  const totalPages = Math.ceil(fontesFiltradas.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = fontesFiltradas.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reseta para primeira página quando mudar o número de itens
  };

  // Resetar para primeira página quando houver uma nova busca
  useEffect(() => {
    setCurrentPage(1);
  }, [busca]);

  // Função para carregar fonte específica
  const carregarFonte = async (arquivo: string) => {
    if (!fontesCarregadas.has(arquivo)) {
      try {
        const success = await loadFontFromCache(arquivo);
        if (success) {
          setFontesCarregadas(prev => new Set([...prev, arquivo]));
        }
      } catch (error) {
        console.error('Erro ao carregar fonte:', error);
      }
    }
  };

  // Carregar fontes quando a lista for atualizada
  useEffect(() => {
    if (fontes) {
      fontes.forEach(fonte => {
        if (fonte.arquivo) {
          carregarFonte(fonte.arquivo);
        }
      });
    }
  }, [fontes]);

  // Modificar o array columns para incluir o estilo da fonte na demonstração
  const columns = [
    { 
      header: 'Nome', 
      accessor: 'nome' as const,
      width: '20%'
    },
    { 
      header: 'Montadora', 
      accessor: 'nome_montadora' as const,
      width: '20%'
    },
    { 
      header: 'Demonstração', 
      accessor: 'demonstracao' as const,
      width: '60%',
      render: (fonte: Fonte) => (
        <div className="w-full">
          <span 
            className="break-all whitespace-normal overflow-hidden block" 
            style={{ 
              fontFamily: fonte.arquivo ? `"${fonte.arquivo.replace('.ttf', '')}"` : 'inherit',
              fontSize: '21px',
              letterSpacing: '2px',
              lineHeight: '1.5',
              wordBreak: 'break-all',
              maxWidth: '100%',
              hyphens: 'auto'
            }}
          >
            {fonte.demonstracao || '-'}
          </span>
        </div>
      )
    },
  ];

  const actions = [
    {
      icon: <Pencil size={20} />,
      onClick: (fonte: Fonte) => navigate(`/fontes/${fonte.id}`),
      className: "text-blue-600 hover:text-blue-900"
    },
    {
      icon: <Trash2 size={20} />,
      onClick: handleExcluir,
      className: "text-red-600 hover:text-red-900"
    }
  ];

  if (listLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <HeaderList
        title="Fontes"
        buttonLabel="Nova Fonte"
        searchPlaceholder="Buscar fontes..."
        searchValue={busca}
        onSearchChange={setBusca}
        onButtonClick={() => navigate('/fontes/novo')}
      />

      <div className="w-full overflow-hidden">
        <TableList
          data={currentItems}
          columns={columns}
          actions={actions}
        />
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={fontesFiltradas.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />

      <ConfirmationModal
        isOpen={!!fonteParaExcluir}
        onClose={() => setFonteParaExcluir(null)}
        onConfirm={confirmarExclusao}
        title="Excluir Fonte"
        message={`Tem certeza que deseja excluir a fonte ${fonteParaExcluir?.nome}?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={listLoading}
      />
    </div>
  );
};

export default FontesLista; 